import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { REVIEWS_LISTING_SEED } from '@common/data';
import type {
  ReviewListItem,
  ReviewListPage,
  ReviewListQuery,
} from '@common/types';

const LIST_LATENCY_MS = 600;

type SortValue = string | number | null | undefined;

const SORT_ACCESSORS: Record<string, (r: ReviewListItem) => SortValue> = {
  date: (r) => r.date,
  displayName: (r) => r.displayName.toLowerCase(),
  email: (r) => r.email?.toLowerCase() ?? null,
  reviewRating: (r) => r.reviewRating,
  nps: (r) => r.kpis.nps ?? null,
  overall: (r) => r.kpis.overallSat ?? null,
  value: (r) => r.kpis.valueForMoney ?? null,
  service: (r) => r.kpis.service ?? null,
  room: (r) => r.kpis.room ?? null,
  sleep: (r) => r.kpis.sleepQuality ?? null,
  cleanliness: (r) => r.kpis.cleanliness ?? null,
  location: (r) => r.kpis.location ?? null,
  wifi: (r) => r.kpis.wifi ?? null,
};

@Injectable({ providedIn: 'root' })
export class ReviewsListingService {
  private readonly reviews = signal<ReviewListItem[]>(REVIEWS_LISTING_SEED.slice());
  private readonly _revision = signal<number>(0);

  readonly revision = this._revision.asReadonly();

  list(query: ReviewListQuery): Observable<ReviewListPage> {
    let result: ReviewListItem[] = this.reviews().slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (r) =>
          r.displayName.toLowerCase().includes(term) ||
          (r.email?.toLowerCase().includes(term) ?? false) ||
          r.source.description.toLowerCase().includes(term),
      );
    }

    const sourceFilters = query.filters['source'];
    if (sourceFilters?.length) {
      const allowed = new Set(sourceFilters);
      result = result.filter((r) => allowed.has(r.source.key));
    }

    const sentimentFilters = query.filters['sentiment'];
    if (sentimentFilters?.length) {
      const allowed = new Set(sentimentFilters);
      result = result.filter((r) => allowed.has(r.sentiment));
    }

    const stateFilters = query.filters['state'];
    if (stateFilters?.length) {
      const allowed = new Set(stateFilters);
      result = result.filter((r) => {
        if (allowed.has('unread') && !r.readFlag) return true;
        if (allowed.has('alerts') && r.hotAlerts > 0) return true;
        if (allowed.has('published') && r.publishFlag) return true;
        if (allowed.has('pending') && r.managementResponsePending) return true;
        return false;
      });
    }

    if (query.sortField) {
      const accessor = SORT_ACCESSORS[query.sortField];
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      if (accessor) {
        result.sort((a, b) => {
          const av = accessor(a);
          const bv = accessor(b);
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          if (av < bv) return -1 * direction;
          if (av > bv) return 1 * direction;
          return 0;
        });
      }
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    const data = result.slice(start, end);

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }
}
